import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PhrasesContext } from "../../context/phrasesContext";
import {
  getAllDetails,
  getCaseTypes,
  getCasesStatus,
  getCourts,
  getServiceCategories,
  getServiceStatus,
} from "../../services/Common";
import Pagination from "../pagination";
import ServiceStatus from "../serviceStatus";
import "./index.css";

export default function Table({
  servicesData,
  isSubTable,
  feedBackIsOpen,
  setFeedBackIsOpen,
  onSurvayRequest,
}) {
  const [requests, setRequests] = useState(servicesData || []);
  const [currentPage, setCurrentPage] = useState(
    Number(localStorage.getItem("currentPage")) || 1,
  );
  const [requestPerPage, setRequestPerPage] = useState(20);
  const [order, setOrder] = useState("ASC");

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [filtered, setFiltered] = useState("");
  const [filteredCaseNumber, setFilteredCaseNumber] = useState("");
  const [filteredServiceName, setFilteredServiceName] = useState("");
  const [filteredCaseTypeName, setFilteredCaseTypeName] = useState("");
  const [filteredCourtName, setFilteredCourtName] = useState("");
  const [filteredStatusCase, setFilteredStatusCase] = useState("");
  const [filteredCreatDate, setFilteredCreatDate] = useState("");
  const [filteredStatus, setFilteredStatus] = useState("");
  const [activeSort, setAciveSort] = useState(false);
  const navigate = useNavigate();
  const inputFile = useRef(null);
  const [lang, setLang] = useState("");
  const [courts, setCourts] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);
  const [caseRequest, setCaseRequest] = useState([]);
  const [servicesStatus, setServicesStatus] = useState([]);
  const [casesStatus, setCasesStatus] = useState([]);
  const [years, setYears] = useState([]);
  const maxOffset = 60;

  const [enPhrase, arPhrase] = useContext(PhrasesContext);

  const getServiceCatrgoryServices = (currentLang) => {
    return getServiceCategories(currentLang).then((res) => {
      setCaseRequest(res);
    });
  };

  const getCaseTypesSrvices = (currentLang) => {
    return getCaseTypes(currentLang).then((res) => {
      setCaseTypes(res);
    });
  };

  const getCourtsTypes = (currentLang) => {
    return getCourts(currentLang).then((res) => {
      setCourts(res);
    });
  };

  const getServicesStatus = (currentLang) => {
    return getServiceStatus(currentLang).then((res) => {
      setServicesStatus(res);
    });
  };

  const getCaseStatus = (currentLang) => {
    return getCasesStatus(currentLang).then((res) => {
      setCasesStatus(res);
    });
  };

  const getDetails = (currentLang) => {
    return getAllDetails(currentLang).then((res) => {
      setRequests(res?.service);
    });
  };

  useEffect(() => {
    const lang = window.localStorage.getItem("lang");
    let currentLang = lang;
    if (lang === "en") {
      document.body.dir = "ltr";
      setLang("en");
      currentLang = "en";
    } else {
      document.body.dir = "rtl";
      setLang("ar");
      currentLang = "ar";
    }
    if (!isSubTable) getDetails(currentLang);
    const year = new Date().getFullYear();
    setYears(Array.from(new Array(maxOffset), (val, index) => year - index));
    getServiceCatrgoryServices(currentLang);
    getCaseTypesSrvices(currentLang);
    getCourtsTypes(currentLang);
    getServicesStatus(currentLang);
    getCaseStatus(currentLang);
  }, []);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleFilterChange = (setterFn) => (e) => {
    setCurrentPage(1); // Crucial: Reset to page 1 whenever filters change
    setterFn(e.target.value);
  };

  const normalize = (v) => {
    if (v === null || v === undefined) return "";
    return v;
  };

  const compare = (a, b, key) => {
    const va = normalize(a?.[key]);
    const vb = normalize(b?.[key]);

    const na = Number(va);
    const nb = Number(vb);
    const bothNumeric = !Number.isNaN(na) && !Number.isNaN(nb) && va !== "" && vb !== "";
    if (bothNumeric) return na - nb;

    if (key === "createdAt") {
      const da = new Date(va).getTime();
      const db = new Date(vb).getTime();
      return da - db;
    }

    return String(va).localeCompare(String(vb), undefined, { sensitivity: "base" });
  };

  // 1. FILTER STEP: Always run filters on the master 'requests' array first
  const filteredRequests = React.useMemo(() => {
    return requests
      .filter((r) => (filtered === "" ? true : String(r?.servicesId ?? "").includes(filtered)))
      .filter((r) => (filteredCaseNumber === "" ? true : String(r?.caseNumber ?? "").includes(filteredCaseNumber)))
      .filter((r) => (filteredServiceName === "" ? true : String(r?.serviceName ?? "").includes(filteredServiceName)))
      .filter((r) => (filteredCaseTypeName === "" ? true : String(r?.caseTypeName ?? "").toLowerCase().includes(filteredCaseTypeName.toLowerCase())))
      .filter((r) => (filteredCourtName === "" ? true : String(r?.courtName ?? "").includes(filteredCourtName)))
      .filter((r) => (filteredStatus === "" ? true : String(r?.serviceStatus ?? "").includes(filteredStatus)))
      .filter((r) => (filteredStatusCase === "" ? true : String(r?.caseStatus ?? "").includes(filteredStatusCase)))
      .filter((r) => (filteredCreatDate === "" ? true : String(r?.createdAt ?? "").includes(filteredCreatDate)));
  }, [requests, filtered, filteredCaseNumber, filteredServiceName, filteredCaseTypeName, filteredCourtName, filteredStatus, filteredStatusCase, filteredCreatDate]);

  // 2. SORT STEP: Sort the dataset after it has been filtered completely
  const sortedRequests = React.useMemo(() => {
    if (!sortConfig.key) return filteredRequests;
    const sorted = [...filteredRequests].sort((a, b) => compare(a, b, sortConfig.key));
    return sortConfig.direction === "asc" ? sorted : sorted.reverse();
  }, [filteredRequests, sortConfig]);

  // 3. PAGINATION STEP: Slice the active page items from the sorted, filtered data array
  const indexOfLastRequest = currentPage * requestPerPage;
  const indexOfFirstRequest = indexOfLastRequest - requestPerPage;
  const pageRows = React.useMemo(() => {
    return sortedRequests.slice(indexOfFirstRequest, indexOfLastRequest);
  }, [sortedRequests, indexOfFirstRequest, indexOfLastRequest]);

  // Map legacy variable names to prevent breaking your current layout template references
  const filtered2 = filteredRequests;
  const mainFilter = filteredRequests;

  const handleChangeRequestPerPage = (event) => {
    setRequestPerPage(Number(event.target.value));
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const sorting = (key) => {
    setCurrentPage(1);
    setSortConfig((prev) => {
      const direction = prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      return { key, direction };
    });
  };

  const handleClick = (value1) => {
    setFeedBackIsOpen(true);
    setTimeout(() => {
      onSurvayRequest(value1);
    }, 100);
  };

  useEffect(() => {
    localStorage.setItem("currentPage", currentPage);
  }, [currentPage]);

  return (
    <div className="flex flex-col w-full">
      <div className="overflow-hidden xs:overflow-scroll sm:overflow-scroll md:overflow-scroll lg:!overflow-scroll lg:!overflow-y-hidden xl:!overflow-hidden">
        <table className="min-w-full text-left text-sm font-light w-full">
          <thead className="bg-[#ffffff] h-[92px] border-[1px] border-solid border-[#E6D7A2] !rounded-[12px]">
            <tr style={{ borderRadius: "20px" }}>
              <th scope="col" className={isSubTable ? (lang === "en" ? "numberRequestThDestalisEn" : "numberRequestThDestalis") : "numberRequestTh"}>
                <div className="Mainth numberRequest">
                  <p style={{ fontSize: "14px", color: "#010202", textAlign: "right", fontFamily: "Almarai", fontWeight: "bold", height: "18px" }} className={`text-[#010202] font-Almarai font-bold h-[18px] ${lang === "en" ? "text-left !text-[12px]" : "text-[14px]"}`}>
                    {lang === "en" ? enPhrase["lblAppNumber"] : arPhrase["lblAppNumber"]}
                  </p>
                  <img style={{ width: "16px", height: "16px" }} src={sortConfig.key === "servicesId" && sortConfig.direction === "desc" ? "/assets/img/case-overview/Active-sort.svg" : "/assets/img/case-overview/sort.svg"} onClick={() => sorting("servicesId")} alt="" />
                </div>
                <div className="sectionInput" style={{ width: "85px" }}>
                  <input className="MainInput" style={{ width: "85px", height: "24px" }} placeholder={lang === "en" ? "App. Number" : "رقم الطلب"} onChange={handleFilterChange(setFiltered)} value={filtered} />
                  <img src="/assets/img/case-overview/close.svg" alt="close-icon" onClick={() => { setFiltered(""); setCurrentPage(1); }} className={lang === "en" ? "sectionInputImgAr" : ""} />
                </div>
              </th>

              <th scope="col" className={isSubTable ? (lang === "en" ? "typeRequestThDetailsEn" : "typeRequestThDetails") : "typeRequestTh"}>
                <div className="typeRequest">
                  <p style={{ fontSize: "14px", color: "#010202", fontFamily: "Almarai", fontWeight: "bold", height: "18px" }} className={`text-[#010202] font-Almarai font-bold h-[18px] ${lang === "en" ? "!text-left !text-[12px]" : "text-right text-[14px]"}`}>
                    {lang === "en" ? enPhrase["lblApplicationTypeDot"] : arPhrase["lblApplicationTypeDot"]}
                  </p>
                </div>
                <div className="sectionInput">
                  <select style={{ height: "24px", width: "135px", fontSize: "12px", padding: "0 10px", fontWeight: "normal", color: "#949494" }} className={lang === "en" ? "selectLineAR focus:border-gold-100 focus:ring-gold-100 !px-[2px] !truncate !pr-[30px] !pl-[5px]" : "selectLine focus:border-gold-100 focus:ring-gold-100 !truncate !pr-[5px] !pl-[30px]"} onChange={handleFilterChange(setFilteredServiceName)} value={filteredServiceName}>
                    <option value="" className="text-slate-200" disabled hidden>{lang === "en" ? enPhrase["lblSelectApplicatioType"] : arPhrase["lblSelectApplicatioType"]}</option>
                    {caseRequest?.map((cr) => (<option key={cr?.id} value={cr?.name}>{cr?.name}</option>))}
                  </select>
                  <img src="/assets/img/case-overview/close.svg" alt="close-icon" onClick={() => { setFilteredServiceName(""); setCurrentPage(1); }} className={lang === "en" ? (isSubTable ? "!absolute !left-[120px]" : "sectionInputImgAr") : "!absolute right-[120px]"} />
                </div>
              </th>

              {!isSubTable && (
                <th scope="col" className="numberCaseTh">
                  <div className="Mainth numberCase">
                    <p style={{ fontSize: "14px", color: "#010202", fontFamily: "Almarai", fontWeight: "bold", height: "18px" }} className={`text-[#010202] font-Almarai font-bold h-[18px] ${lang === "en" ? "!text-left !text-[12px]" : "text-[14px]"}`}>
                      {lang === "en" ? "Case Num" : "رقم القضيه"}
                    </p>
                    <img style={{ width: "16px", height: "16px" }} src="/assets/img/case-overview/sort.svg" onClick={() => sorting("caseNumber")} alt="" />
                  </div>
                  <div className="sectionInput" style={{ width: "95px" }}>
                    <input className="MainInput" placeholder={lang === "en" ? "Case Number" : "رقم القضيه"} onChange={handleFilterChange(setFilteredCaseNumber)} value={filteredCaseNumber} />
                    <img src="/assets/img/case-overview/close.svg" alt="close-icon" onClick={() => { setFilteredCaseNumber(""); setCurrentPage(1); }} className={lang === "en" ? "sectionInputImgAr" : ""} />
                  </div>
                </th>
              )}

              {!isSubTable && (
                <th scope="col" className="typeCourtTh">
                  <div className="typeCourt">
                    <p style={{ fontSize: "14px", color: "#010202", fontFamily: "Almarai", fontWeight: "bold", height: "18px" }} className={`text-[#010202] font-Almarai font-bold h-[18px] ${lang === "en" ? "!text-left !text-[12px]" : "text-[14px]"}`}>
                      {lang === "en" ? enPhrase["lblCaseType"] : arPhrase["lblCaseType"]}
                    </p>
                  </div>
                  <div className="sectionInput">
                    <select style={{ height: "24px", width: "100%", fontSize: "12px", padding: "0 10px", fontWeight: "normal", color: "#949494" }} className={lang === "en" ? "selectLineEn focus:border-gold-100 focus:ring-gold-100 !px-[2px] !truncate !pr-[30px] !pl-[5px]" : "selectLine focus:border-gold-100 focus:ring-gold-100 !truncate !pr-[5px] !pl-[30px]"} onChange={handleFilterChange(setFilteredCaseTypeName)} value={filteredCaseTypeName}>
                      <option value="" className="text-slate-200" disabled hidden>{lang === "en" ? enPhrase["lblSelectCaseType"] : arPhrase["lblSelectCaseType"]}</option>
                      {caseTypes?.map((ct) => (<option key={ct?.id} value={ct?.name}>{ct?.name}</option>))}
                    </select>
                    <img src="/assets/img/case-overview/close.svg" alt="close-icon" onClick={() => { setFilteredCaseTypeName(""); setCurrentPage(1); }} className={lang === "en" ? "sectionInputImgAr" : ""} />
                  </div>
                </th>
              )}

              {!isSubTable && (
                <th scope="col" className="courtTh">
                  <div className="court">
                    <p style={{ fontSize: "14px", color: "#010202", fontFamily: "Almarai", fontWeight: "bold", height: "18px" }} className={`text-[#010202] font-Almarai font-bold h-[18px] ${lang === "en" ? "!text-left !text-[12px]" : "text-[14px]"}`}>
                      {lang === "en" ? enPhrase["lblCourt"] : arPhrase["lblCourt"]}
                    </p>
                  </div>
                  <div className="sectionInput">
                    <select style={{ height: "24px", width: "100%", fontSize: "12px", padding: "0 5px 0 30px", fontWeight: "normal", color: "#949494" }} className={lang === "en" ? "selectLineAR focus:border-gold-100 focus:ring-gold-100 !px-[2px] !truncate !pr-[30px] !pl-[5px]" : "selectLine focus:border-gold-100 focus:ring-gold-100 !truncate !pr-[5px] !pl-[30px]"} onChange={handleFilterChange(setFilteredCourtName)} value={filteredCourtName}>
                      <option value="" className="text-slate-200" disabled hidden>{lang === "en" ? enPhrase["lblCourt"] : arPhrase["lblCourt"]}</option>
                      {courts?.map((c) => (<option key={c?.id} value={c?.name}>{c?.name}</option>))}
                    </select>
                    <img src="/assets/img/case-overview/close.svg" alt="close-icon" onClick={() => { setFilteredCourtName(""); setCurrentPage(1); }} className={lang === "en" ? "sectionInputImgAr" : ""} />
                  </div>
                </th>
              )}

              <th scope="col" className="dateRequestTh">
                <div className="dateRequest">
                  <p style={{ fontSize: "14px", color: "#010202", fontFamily: "Almarai", fontWeight: "bold", height: "18px" }} className={`text-[#010202] font-Almarai font-bold h-[18px] ${lang === "en" ? "!text-left !text-[12px]" : "text-[14px]"}`}>
                    {lang === "en" ? enPhrase["lblCreatedDate"] : arPhrase["lblCreatedDate"]}
                  </p>
                </div>
                <div className="sectionInput" style={{ width: "95px" }}>
                  <select style={{ height: "24px", width: "95px", fontSize: "12px", padding: "0 10px", fontWeight: "normal", color: "#949494" }} className={lang === "en" ? "selectLineAR focus:border-gold-100 focus:ring-gold-100 !px-[2px]" : "selectLine focus:border-gold-100 focus:ring-gold-100"} onChange={handleFilterChange(setFilteredCreatDate)} value={filteredCreatDate}>
                    <option value="" className="text-slate-200" disabled hidden>{lang === "en" ? "Date" : "اختر تاريخ"}</option>
                    {years.map((y) => (<option key={y} value={y}>{y}</option>))}
                  </select>
                  <img src="/assets/img/case-overview/close.svg" alt="close-icon" onClick={() => { setFilteredCreatDate(""); setCurrentPage(1); }} className={lang === "en" ? "sectionInputImgAr !right-[0px]" : "!left-[0px]"} />
                </div>
              </th>

              <th scope="col" className={isSubTable ? (lang === "en" ? "requestStatusThDetailsEn" : "requestStatusThDetails") : "requestStatusTh"}>
                <div className="requestStatus">
                  <p style={{ fontSize: "14px", color: "#010202", fontFamily: "Almarai", fontWeight: "bold", height: "18px" }} className={`text-[#010202] font-Almarai font-bold h-[18px] ${lang === "en" ? "!text-left !text-[12px]" : "text-[14px]"}`}>
                    {lang === "en" ? enPhrase["lblApplicationStatus"] : arPhrase["lblApplicationStatus"]}
                  </p>
                </div>
                <div className="sectionInput" style={{ width: "145px" }}>
                  <select style={{ height: "24px", width: "145px", fontSize: "12px", padding: "0 10px", fontWeight: "normal", color: "#949494" }} className={lang === "en" ? "selectLineAR focus:border-gold-100 focus:ring-gold-100 !px-[2px]" : "selectLine focus:border-gold-100 focus:ring-gold-100"} onChange={handleFilterChange(setFilteredStatus)} value={filteredStatus}>
                    <option value="" className="text-slate-200" disabled hidden>{lang === "en" ? enPhrase["lblSelectChooseOrderStatus"] : arPhrase["lblSelectChooseOrderStatus"]}</option>
                    {servicesStatus?.map((ss) => (<option key={ss?.id} value={ss?.name}>{ss?.name}</option>))}
                  </select>
                  <img src="/assets/img/case-overview/close.svg" alt="close-icon" onClick={() => { setFilteredStatus(""); setCurrentPage(1); }} className={lang === "en" ? "sectionInputImgAr" : ""} />
                </div>
              </th>

              <th scope="col" style={{ width: "130px" }}></th>
            </tr>
          </thead>

          {filtered2.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={8}>
                  <p className="text-[18px] text-center !mt-[25px] w-full font-bold text-red-500">
                    {lang === "en" ? enPhrase["lblThereAreApplications"] : arPhrase["lblThereAreApplications"]}
                  </p>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {pageRows?.map((request, index) => (
                <tr key={index}   className="border-b border-[#E1E1E1] bg-white text-[#010202]">
                  <td className={lang === "en" ? "numberRequestTdEn" : "numberRequestTd"} style={{ width: "85px" }}>
                    {request?.servicesId}
                  </td>
                  <td className={lang === "en" ? "typeRequestTdEn" : "typeRequestTd"} onClick={() => window.scrollTo(0, 0)}>
                    <Link to={`/order-details/${request?.caseId}/${request?.servicesId}`} className="flex transition-all duration-300 font-medium py-2 underline text-[#AF8C45]" style={{ width: "140px" }}>
                      <p style={{ width: "140px", textOverflow: "ellipsis", textWrap: "balance" }}>
                        {request?.serviceName}
                      </p>
                    </Link>
                  </td>
                  {!isSubTable && (
                    <td className={lang === "en" ? "numberCaseTdEn" : "numberCaseTd"}>
                      {request?.caseId !== 0 && request?.caseNumber !== null ? (
                        <Link onClick={(e) => { e.preventDefault(); navigate(`/case-details/${request?.caseId}`); }} to={`/case-details/${request?.caseId}`} className={`flex transition-all duration-300 font-medium py-2 ${lang === "en" ? "numberCaseTdEntext" : "numberCasetext"}`}>
                          {request?.caseNumber?.slice(-12)}
                        </Link>
                      ) : (
                        <p className={`font-medium py-2 text-black ${lang === "en" ? "numberCaseTdEntextPending" : "numberCasetextPending"}`} style={{ width: "95px", textOverflow: "ellipsis", textWrap: "balance" }}>
                          {request?.caseNumber?.slice(-12)}
                        </p>
                      )}
                    </td>
                  )}
                  {!isSubTable && (
                    <td className={lang === "en" ? "typeCourtTdEn" : "typeCourtTd"}>
                      {request?.caseTypeName}
                    </td>
                  )}
                  {!isSubTable && (
                    <td className={lang === "en" ? "courtTdEn" : "courtTd"}>
                      {request?.courtName}
                    </td>
                  )}

                  <td className={lang === "en" ? "dateRequestTdEn font-Almarai" : "dateRequestTd"}>
                    {new Date(request?.createdAt).toLocaleDateString("en-GB")}
                  </td>

                  <td className={lang === "en" ? "requestStatusTdEn" : "requestStatusTd"}>
                    <div className="flex items-center gap-2">
                      <ServiceStatus serviceStatusId={request?.serviceStatusId} />
                      <span>{request?.serviceStatus}</span>
                    </div>
                  </td>

                  <td className={lang === "en" ? "actionTdEn" : "actionTd"}>
                    {request?.serviceStatusId === 2 || request?.serviceStatusId === 3 ? (
                      <Link to={`/order-details/${request?.caseId}/${request?.servicesId}`} className="flex gap-[4px] underline decoration-[#af8c45]" onClick={() => window.scrollTo(0, 0)}>
                        <p>{request?.serviceStatusId === 2 ? (lang === "en" ? enPhrase["lblCompleteData"] : arPhrase["lblCompleteData"]) : (lang === "en" ? enPhrase["lblPay"] : arPhrase["lblPay"])}</p>
                        <img src="/assets/img/case-overview/chevron--sort--down.svg" alt="" className={lang === "en" ? "actionSectionImgEn" : ""} />
                      </Link>
                    ) : request?.serviceStatusId === 5 && request?.completeSurvey === false ? (
                      <p className="gap-[4px] underline decoration-[#af8c45] flex cursor-pointer" onClick={() => handleClick(request)}>
                        <span>{lang === "en" ? enPhrase["lbl_Survey"] : arPhrase["lbl_Survey"]}</span>
                        <img src="/assets/img/case-overview/chevron--sort--down.svg" alt="" className={lang === "en" ? "actionSectionImgEn" : ""} />
                      </p>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>

      {filteredRequests.length > 0 && (
        <div className="flex items-center flex-col gap-[24px] justify-center lg:flex-row mt-[24px] mb-[85.5px]">
          <div className="overview-FooterPages">
            <p className="overview-FooterText">
              {lang === "en" ? enPhrase["lblNumberOfApplications"] : arPhrase["lblNumberOfApplications"]}
            </p>
            <select className={lang === "en" ? "overview-FooterDropDownEn focus:border-gold-100 focus:ring-gold-100" : "overview-FooterDropDown focus:border-gold-100 focus:ring-gold-100"} value={requestPerPage} onChange={handleChangeRequestPerPage}>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="40">40</option>
            </select>
          </div>
          
          <div className="overview-FooterTextBold text-center flex items-center justify-center lg:ms-auto">
            <p className="flex w-full">
              {indexOfFirstRequest + 1} - {Math.min(indexOfLastRequest, filteredRequests.length)}
              <span className="mx-1">{lang === "en" ? "From" : "من"}</span>
              {filteredRequests.length}
              <span className="ms-1">{lang === "en" ? enPhrase["lblRequest"] : arPhrase["lblRequest"]}</span>
            </p>
          </div>

          <div className="overview-Footer">
            <Pagination
              requestPerPage={requestPerPage}
              totalRequests={filteredRequests.length}
              paginate={paginate}
              currentPage={currentPage}
              filtered2={filteredRequests}
              lang={lang}
              isMobile={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}
